/**
 * Market tab (spec §7.3; layout per the core-loop plan CL-4): one trade row component for ores and goods, the
 * sections in the model's `firstSection` order (Buy first unless something aboard sells here), a one-line hold message
 * instead of disabled Sell rows, Sell all on top of the Sell section while carrying cargo, the hold's components and
 * data cores only when held, and a footer pointing at the map's Routes layer for prices further out.
 *
 * A trade row: the name; a sub line (stock or hold · what fits · the after-trade price · the better-price hint); on a
 * buy row the best sale in this system with its margin (`market-bestsale-<id>`, glyph and sign, never colour alone);
 * the unit price; one stock cell (the saturation meter and, under the strategic economy, the stock bar); the stepper;
 * the button. A focused row steps its quantity with left/right and trades on activate. Rows are compact under
 * `max-height: 760px` so the first buy rows sit above the fold at 1280×720.
 *
 * Goods (economy plan SY-2c): goods rows follow the ware rows in the same Sell and Buy sections and focus groups.
 * Hidden when the model has no goods rows (`goods.tradeEnabled` off, or M2's own market).
 *
 * Break down (economy plan SY-3): a component row with a `breakDown` preview gets a "Break down" button beside it
 * (goods into the hold). No row has one while `goods.tradeEnabled` is off, so the rows are unchanged then.
 * The buttons form the `G.breakDown` focus group (right from a component row, left back; Phase 1 follow-ups).
 *
 * Deliver (economy plan SY-7): a data core row with a `deliver` option gets a "Deliver" button after its Break down
 * button (standing with the station's faction, no credits). It joins the same `G.breakDown` row-button group, in DOM
 * order (`rowButtonIndex`), so keyboard and gamepad reach it like the Break down buttons.
 *
 * Read for fragments (M8 plan MC-h5b, D-516): at a Mutual market station a generic data core row with a `read` option
 * gets a "Read for blueprint fragments" button after Deliver (`UiActions.craft` `dataCore.decode`), in the same
 * `G.breakDown` group. Its text comes from `blueprints.json` and never says "decode" (M7 Mission 1 owns that word).
 *
 * After-trade price (M8 plan TR-4, CL-3): every row with a quantity adds "Your trade moves the price to X" for it
 * (`afterTradeLabel` from the `quoteGood` port for goods, `wareAfterTradeLabel` from the `quoteWare` port for ores).
 *
 * Rush spares (M8 plan MC-h4a): at a Holdfast market station the Rush spares goods row carries a `rushSale`: it steps
 * whole spares and sells through `UiActions.craft` (`rushSpares.sell`), never `sellGood` (which refuses them).
 */
import { useState, type ReactNode } from 'react'
import type { GoodId } from '../../career/parts'
import type { WareId } from '../../data/m2Schemas'
import { FocusButton, Meter, Stepper, useFocusable } from '../focus'
import { formatCr, formatTonnes } from '../format'
import { G } from '../models/layout'
import {
  afterTradeLabel,
  goodSellStep,
  rowButtonIndex,
  sellAllOnTop,
  wareAfterTradeLabel,
  type BestSale,
  type ComponentRow,
  type GoodBuyRow,
  type GoodSellRow,
  type MarketModel,
  type WareBuyRow,
  type WareSellRow,
} from '../models/market'
import { clampQuantity, stepQuantity } from '../models/stepper'
import { RowEconomyCells } from '../strategic/CauseChip'
import type { TenderView } from '../contracts'
import type { ScreenProps } from './common'

type Qty = Partial<Record<WareId, number>>
type GoodQty = Partial<Record<GoodId, number>>

/** The margin's direction for styling only (the label carries the glyph and the sign). */
function marginDir(b: BestSale): 'up' | 'down' | 'flat' | 'none' {
  if (b.margin === null) return 'none'
  const p = Math.round(b.margin * 100)
  return p > 0 ? 'up' : p < 0 ? 'down' : 'flat'
}

interface TradeLineProps {
  group: string
  index: number
  /** Ware or good id (the best-sale test id). */
  id: string
  name: string
  tint?: string
  /** The sub line's parts, joined with " · " (nulls skipped). */
  sub: ReactNode[]
  bestSale?: BestSale
  /** Under the sub line (the berth offer). */
  extra?: ReactNode
  price: string
  /** The one stock cell (meter, stock bar). */
  stock: ReactNode
  qty: number
  stepDisabled: boolean
  stepLabel: string
  onStep: (d: 1 | -1) => void
  activate: () => void
  button: { testId: string; label: string; enabled: boolean }
}

/** CL-4: the one trade row, for ores and goods, buying and selling. */
function TradeLine(p: TradeLineProps): ReactNode {
  const f = useFocusable(p.group, p.index, { activate: p.activate, adjust: p.onStep })
  const sub = p.sub.filter((s) => s !== null && s !== undefined && s !== false)
  return (
    <div className="ui-row ui-row--trade" {...f.props}>
      <span className="ui-swatch" style={p.tint !== undefined ? { background: p.tint } : undefined} aria-hidden="true" />
      <span className="ui-row__main">
        <span className="ui-row__title">{p.name}</span>
        <span className="ui-row__sub">
          {sub.map((s, i) => (
            <span key={i}>
              {i > 0 ? ' · ' : ''}
              {s}
            </span>
          ))}
        </span>
        {p.bestSale !== undefined ? (
          <span className="ui-row__sub ui-market__best" data-testid={`market-bestsale-${p.id}`} data-margin={marginDir(p.bestSale)}>
            {p.bestSale.label}
          </span>
        ) : null}
        {p.extra}
      </span>
      <span className="ui-row__price">{p.price}</span>
      <span className="ui-market__stock">{p.stock}</span>
      <Stepper value={`${formatTonnes(p.qty)} t`} onStep={p.onStep} disabled={p.stepDisabled} label={p.stepLabel} />
      <button type="button" tabIndex={-1} className="ui-btn ui-btn--small" data-testid={p.button.testId} disabled={!p.button.enabled} onClick={p.activate}>
        {p.button.label}
      </button>
    </div>
  )
}

interface RowProps<R> extends ScreenProps {
  row: R
  index: number
  qty: number
  setQty: (q: number) => void
}

function WareSellLine(p: RowProps<WareSellRow>): ReactNode {
  const { row, ctl } = p
  const step = ctl.cfg.steps.tonnes
  const can = row.maxT > 0 && p.qty > 0
  const sell = (): void => {
    if (can) ctl.run(() => ctl.actions.sellWare(row.ware, p.qty))
  }
  // PT-2 (D-803): the rest that doesn't fit here can go to the pilot's berth (`berth.store`, M8 MC-b4).
  const rest = row.storeRest
  const craft = ctl.actions.craft
  const storeRest = (): void => {
    if (rest !== undefined && craft !== undefined) ctl.run(() => craft({ kind: 'berth.store', stationId: rest.stationId, sel: { wares: { [row.ware]: rest.tonnes } } }))
  }
  const after = can ? wareAfterTradeLabel(ctl.actions.quoteWare, ctl.data.wares.market.saturation.k, row.ware, p.qty, 'sell', row.unit) : null
  return (
    <TradeLine
      group={G.sell}
      index={p.index}
      id={row.ware}
      name={row.name}
      tint={row.tint}
      sub={[
        `${row.heldLabel} in the hold`,
        row.roomLabel !== undefined ? <span data-testid={`market-room-${row.ware}`}>{row.roomLabel}</span> : null,
        after !== null ? <span className="ui-hint" data-testid={`market-after-sell-${row.ware}`}>{after}</span> : null,
        row.hint !== null ? <span className="ui-hint">{'▲'} {row.hint.label}</span> : null,
      ]}
      extra={
        rest !== undefined && craft !== undefined ? (
          <button type="button" tabIndex={-1} className="ui-btn ui-btn--small" data-testid={`market-store-rest-${row.ware}`} onClick={storeRest}>
            {rest.label}
          </button>
        ) : undefined
      }
      price={row.unitLabel}
      stock={
        <>
          {row.unit !== null ? <Meter value={row.saturation} label={row.saturationLabel} tone={row.saturation > 0.15 ? 'warning' : 'ok'} /> : null}
          <RowEconomyCells economy={row.economy} />
        </>
      }
      qty={p.qty}
      stepDisabled={row.maxT <= 0}
      stepLabel="tonnes to sell"
      onStep={(d) => p.setQty(stepQuantity(p.qty, d, step, row.maxT))}
      activate={sell}
      button={{ testId: `market-sell-${row.ware}`, label: 'Sell', enabled: can }}
    />
  )
}

function WareBuyLine(p: RowProps<WareBuyRow>): ReactNode {
  const { row, ctl } = p
  const step = ctl.cfg.steps.tonnes
  const can = row.maxT > 0 && p.qty > 0
  const buy = (): void => {
    if (can) ctl.run(() => ctl.actions.buyWare(row.ware, p.qty))
  }
  const after = can ? wareAfterTradeLabel(ctl.actions.quoteWare, ctl.data.wares.market.saturation.k, row.ware, p.qty, 'buy', row.unit) : null
  return (
    <TradeLine
      group={G.buy}
      index={p.index}
      id={row.ware}
      name={row.name}
      tint={row.tint}
      sub={[`Up to ${formatTonnes(row.maxT)} t fits your hold and wallet`, after !== null ? <span className="ui-hint" data-testid={`market-after-buy-${row.ware}`}>{after}</span> : null]}
      bestSale={row.bestSale}
      price={row.unitLabel}
      stock={
        <>
          <Meter value={row.saturation} label={row.saturationLabel} />
          <RowEconomyCells economy={row.economy} />
        </>
      }
      qty={p.qty}
      stepDisabled={row.maxT <= 0}
      stepLabel="tonnes to buy"
      onStep={(d) => p.setQty(stepQuantity(p.qty, d, step, row.maxT))}
      activate={buy}
      button={{ testId: `market-buy-${row.ware}`, label: can ? `Buy ${formatCr(p.qty * row.unit)}` : 'Buy', enabled: can }}
    />
  )
}

function GoodSellLine(p: RowProps<GoodSellRow>): ReactNode {
  const { row, ctl } = p
  const step = goodSellStep(row, ctl.cfg.steps.tonnes)
  const rush = row.rushSale
  const can = row.max > 0 && p.qty > 0 && (rush === undefined ? ctl.actions.sellGood !== undefined : ctl.actions.craft !== undefined)
  const sell = (): void => {
    if (!can) return
    if (rush !== undefined) {
      const craft = ctl.actions.craft
      if (craft !== undefined) ctl.run(() => craft({ kind: 'rushSpares.sell', stationId: rush.stationId, qty: p.qty }))
      return
    }
    const act = ctl.actions.sellGood
    if (act !== undefined) ctl.run(() => act(row.good, p.qty))
  }
  const after = row.max > 0 && rush === undefined ? afterTradeLabel(ctl.actions.quoteGood, row.good, p.qty, 'sell', row.unit) : null
  return (
    <TradeLine
      group={G.sell}
      index={p.index}
      id={row.good}
      name={row.name}
      sub={[
        `${row.heldLabel} in the hold`,
        after !== null ? <span className="ui-hint" data-testid={`market-goods-after-sell-${row.good}`}>{after}</span> : null,
        row.hint !== null ? <span className="ui-hint" data-testid={`market-goods-hint-${row.good}`}>{'▲'} {row.hint.label}</span> : null,
        rush !== undefined ? (
          <span className="ui-hint" data-testid="market-rush-sale">
            {rush.label}
            {rush.standingLabel !== null ? ` · ${rush.standingLabel}` : ''}
          </span>
        ) : null,
      ]}
      price={row.unitLabel}
      stock={null}
      qty={p.qty}
      stepDisabled={row.max <= 0}
      stepLabel="tonnes to sell"
      onStep={(d) => p.setQty(stepQuantity(p.qty, d, step, row.max))}
      activate={sell}
      button={{ testId: `market-goods-sell-${row.good}`, label: 'Sell', enabled: can }}
    />
  )
}

function GoodBuyLine(p: RowProps<GoodBuyRow>): ReactNode {
  const { row, ctl } = p
  const step = ctl.cfg.steps.tonnes
  const can = row.max > 0 && p.qty > 0 && ctl.actions.buyGood !== undefined
  const buy = (): void => {
    const act = ctl.actions.buyGood
    if (can && act !== undefined) ctl.run(() => act(row.good, p.qty))
  }
  const after = row.max > 0 ? afterTradeLabel(ctl.actions.quoteGood, row.good, p.qty, 'buy', row.unit) : null
  return (
    <TradeLine
      group={G.buy}
      index={p.index}
      id={row.good}
      name={row.name}
      sub={[
        `${formatTonnes(row.stock)} t in stock`,
        `up to ${formatTonnes(row.max)} t fits your hold and wallet`,
        after !== null ? <span className="ui-hint" data-testid={`market-goods-after-buy-${row.good}`}>{after}</span> : null,
      ]}
      {...(row.bestSale !== undefined ? { bestSale: row.bestSale } : {})}
      price={row.unitLabel}
      stock={null}
      qty={p.qty}
      stepDisabled={row.max <= 0}
      stepLabel="tonnes to buy"
      onStep={(d) => p.setQty(stepQuantity(p.qty, d, step, row.max))}
      activate={buy}
      button={{ testId: `market-goods-buy-${row.good}`, label: can ? `Buy ${formatCr(p.qty * row.unit)}` : 'Buy', enabled: can }}
    />
  )
}

/** `buttonIndex`: the row's first button's index in `G.breakDown` (the row buttons, in DOM order; SY-3, SY-7). */
function ComponentLine(p: ScreenProps & { row: ComponentRow; index: number; buttonIndex: number }): ReactNode {
  const { row, ctl } = p
  const line = (
    <FocusButton
      group={G.components}
      index={p.index}
      className="ui-row ui-row--part"
      disabled={!row.sellable}
      testId={`market-sellpart-${row.id}`}
      onActivate={() => ctl.run(() => ctl.actions.sellComponent(row.id))}
    >
      <span className="ui-row__main">
        <span className="ui-row__title">
          {row.name}
          {row.note !== null ? <span className="ui-tag" data-kind="contract">{row.note}</span> : null}
        </span>
        <span className="ui-row__sub">
          {row.conditionLabel} condition {'·'} {row.massLabel}
          {row.hint !== null ? <span className="ui-hint"> {'▲'} {row.hint.label}</span> : null}
        </span>
      </span>
      <span className="ui-row__price">{row.sellable ? `Sell ${row.priceLabel}` : row.priceLabel}</span>
    </FocusButton>
  )
  const breakDown = ctl.actions.breakDown
  const preview = breakDown === undefined ? undefined : row.breakDown
  const deliverCore = ctl.actions.deliverCore
  const deliver = deliverCore === undefined ? undefined : row.deliver
  const craft = ctl.actions.craft
  const read = craft === undefined ? undefined : row.read
  if (preview === undefined && deliver === undefined && read === undefined) return line
  const deliverIndex = p.buttonIndex + (preview === undefined ? 0 : 1)
  const readIndex = deliverIndex + (deliver === undefined ? 0 : 1)
  return (
    <div className="ui-row-group">
      {line}
      {preview !== undefined && breakDown !== undefined ? (
        <FocusButton
          group={G.breakDown}
          index={p.buttonIndex}
          className="ui-btn--small"
          testId={`market-breakdown-${row.id}`}
          disabled={!preview.enabled}
          title={preview.enabled ? undefined : 'Not enough cargo space for the goods'}
          onActivate={() => ctl.run(() => breakDown(row.id))}
        >
          Break down {'→'} {preview.label}
        </FocusButton>
      ) : null}
      {deliver !== undefined && deliverCore !== undefined ? (
        <FocusButton
          group={G.breakDown}
          index={deliverIndex}
          className="ui-btn--small"
          testId={`market-deliver-${row.id}`}
          disabled={!deliver.enabled}
          title={deliver.enabled ? undefined : 'Your standing there cannot rise any further'}
          onActivate={() => ctl.run(() => deliverCore(row.id))}
        >
          {deliver.label}
        </FocusButton>
      ) : null}
      {read !== undefined && craft !== undefined ? (
        <FocusButton
          group={G.breakDown}
          index={readIndex}
          className="ui-btn--small"
          testId={`market-read-${row.id}`}
          disabled={!read.enabled}
          title={read.title ?? undefined}
          onActivate={() => ctl.run(() => craft({ kind: 'dataCore.decode', stationId: read.stationId, coreId: row.id }))}
        >
          {read.label}
        </FocusButton>
      ) : null}
    </div>
  )
}

/** M10 P5a8a: moored trades queue for one tender run ("Tender: n t queued", Send tender, the run's bar). */
function TenderStrip(p: ScreenProps & { tender: TenderView }): ReactNode {
  const t = p.tender
  const send = p.ctl.actions.sendTender
  return (
    <section className="ui-section" data-testid="market-tender">
      <h3 className="ui-section__title">{`Tender: ${formatTonnes(t.queuedT)} queued`}</h3>
      {t.running ? <Meter value={t.progress} label={`Tender out: ${Math.ceil(t.remainingS)} s`} /> : null}
      <FocusButton group={G.sellAll} index={0} testId="market-tender-send" disabled={send === undefined || t.running || t.lines === 0} onActivate={() => p.ctl.run(() => (send !== undefined ? send() : { ok: true }))}>
        Send tender
      </FocusButton>
    </section>
  )
}

export function MarketTab(p: ScreenProps & { model: MarketModel }): ReactNode {
  const { model, ctl } = p
  const [sellQty, setSellQty] = useState<Qty>({})
  const [buyQty, setBuyQty] = useState<Qty>({})
  const [goodSellQty, setGoodSellQty] = useState<GoodQty>({})
  const [goodBuyQty, setGoodBuyQty] = useState<GoodQty>({})
  const step = ctl.cfg.steps.tonnes
  const t = ctl.cfg.market
  const hasSellRows = model.sell.length + model.goodsSell.length > 0
  const empty = !hasSellRows && model.components.length === 0
  // CL-4: one line about the hold instead of disabled Sell rows (where it sells with Buy first; or that it's empty).
  const holdLine = model.holdSummary ?? (empty ? t.holdEmpty : null)
  const top = sellAllOnTop(model)
  const sellAll = (
    <>
      {model.tender !== undefined ? <TenderStrip ctl={ctl} tender={model.tender} /> : null}
      <div className="ui-actions">
        <FocusButton group={G.sellAll} index={model.tender !== undefined ? 1 : 0} testId="market-sellall" className="ui-btn--wide" disabled={!model.canSellAll} onActivate={() => ctl.run(() => ctl.actions.sellAllCargo())}>
          {model.sellAllLabel}
        </FocusButton>
      </div>
    </>
  )
  const sellSection = hasSellRows ? (
    <section className="ui-section" data-testid="market-section-sell" key="sell">
      <h3 className="ui-section__title">{t.sections.sell}</h3>
      {top ? sellAll : null}
      {model.sell.map((row, i) => (
        <WareSellLine
          key={row.ware}
          ctl={ctl}
          row={row}
          index={i}
          qty={clampQuantity(sellQty[row.ware] ?? row.maxT, step, row.maxT)}
          setQty={(q) => setSellQty((s) => ({ ...s, [row.ware]: q }))}
        />
      ))}
      {model.goodsSell.map((row, i) => (
        <GoodSellLine
          key={`sell-${row.good}`}
          ctl={ctl}
          row={row}
          index={model.sell.length + i}
          qty={clampQuantity(goodSellQty[row.good] ?? row.max, goodSellStep(row, step), row.max)}
          setQty={(q) => setGoodSellQty((s) => ({ ...s, [row.good]: q }))}
        />
      ))}
    </section>
  ) : null
  const buySection =
    model.buy.length + model.goodsBuy.length > 0 ? (
      <section className="ui-section" data-testid="market-section-buy" key="buy">
        <h3 className="ui-section__title">{t.sections.buy}</h3>
        {model.buy.map((row, i) => (
          <WareBuyLine
            key={row.ware}
            ctl={ctl}
            row={row}
            index={i}
            qty={clampQuantity(buyQty[row.ware] ?? ctl.cfg.buyDefaultT, step, row.maxT)}
            setQty={(q) => setBuyQty((s) => ({ ...s, [row.ware]: q }))}
          />
        ))}
        {model.goodsBuy.map((row, i) => (
          <GoodBuyLine
            key={`buy-${row.good}`}
            ctl={ctl}
            row={row}
            index={model.buy.length + i}
            qty={clampQuantity(goodBuyQty[row.good] ?? ctl.cfg.buyDefaultT, step, row.max)}
            setQty={(q) => setGoodBuyQty((s) => ({ ...s, [row.good]: q }))}
          />
        ))}
      </section>
    ) : null
  return (
    <div className="ui-tab ui-market" data-first={model.firstSection}>
      {holdLine !== null ? (
        <p className="ui-market__hold" data-testid="market-hold">
          {holdLine}
        </p>
      ) : null}
      {model.firstSection === 'buy' ? [buySection, sellSection] : [sellSection, buySection]}
      {model.components.length > 0 ? (
        <section className="ui-section" data-testid="market-components">
          <h3 className="ui-section__title">{t.sections.parts}</h3>
          {model.components.map((row, i) => (
            <ComponentLine key={row.id} ctl={ctl} row={row} index={i} buttonIndex={rowButtonIndex(model.components, i, ctl.actions)} />
          ))}
        </section>
      ) : null}
      {top ? null : sellAll}
      <p className="ui-market__footer" data-testid="market-routes">
        {t.routesFooter}
      </p>
      {model.receipt !== null ? (
        <p className="ui-seed ui-seed--paper" data-testid="market-receipt">
          {model.receipt}
        </p>
      ) : null}
    </div>
  )
}
